import type MockAdapter from 'axios-mock-adapter'
import { auditFieldsForCreate, auditFieldsForUpdate, currentUserId, newId } from '../engine/context'
import { findRow, insertRow, readTable, updateRow, writeTable } from '../store/localDb.store'
import type { LocalRow } from '../types/localDb.types'
import { fail, handle, idOf, nextDocNo, pad, parse, requireRole, txLog } from './c07.shared'
import { notify, userName, wmsRoute } from './notification.shared'
import { materialCodeProblem, normalizeMaterialCode, proposeMaterialCode } from './pendataanMaterialCode'
import {
  labelProposal,
  normName,
  type PackPart,
  PD_CATEGORIES,
  PD_FROM_MASTER,
  PD_LOST_REASONS,
  PD_REPRINT_REASONS,
  PD_SALDO_REASONS,
  PD_STD,
  PD_TO_MASTER,
  PD_UNIT_STATUS,
  type PdCategory,
  parseSaldo,
  pdCategoryOf
} from './pendataanRules'
import { binsOfRm, racksOfRm } from './rackScope'

/**
 * Pendataan Awal RM v7.50 (TAT-01 / TAT-02, laporan 23 P-01…P-09) — `/api/v1/wms/setup-tat/pd/*`.
 *
 * Alur: impor Excel (nama material + saldo bulat) → kemasan (Master Unit) + isi per kemasan per material → usulan
 * jumlah label (penuh + satu parsial) → setujui → cetak stiker (satu `rm_unit` per kemasan, status
 * `AWAITING_FIRST_OPNAME`, flag `OPENING`) → opname pertama: Admin membagi rack, Mulai Opname, operator scan posisi
 * lalu stiker di posisi itu dan mengirim hasil per rack → Admin menyatakan Hilang (setelah semua rack terkirim) →
 * Tutup Opname → Supervisor WH RM menyetujui sekali (unit In Stock di posisi scan + `stock_movement` OPENING) atau
 * mengembalikan dengan catatan (rack dibuka lagi). Tidak ada pembekuan gudang dan tidak ada stok awal tanpa label.
 *
 * Tabel (usulan ERD bagian 19): `initial_capture_session` (sesi), `rm_opening_stock` (dipakai ulang sebagai baris
 * pemetaan), `initial_capture_area` (satu baris per rack: operator + waktu kirim), `initial_capture_scan` (hasil scan:
 * PRESENT / EMPTY / LOST), `rm_unit` (stiker).
 */

const ROLE = {
  admin: 'admin_wh_rm',
  operator: 'operator_wh_rm',
  supervisor: 'wh_rm_supervisor'
} as const
const LOG_TYPE = 'Pendataan Awal'

export * from './pendataanRules'

const fmtId = (value: number) => new Intl.NumberFormat('id-ID').format(value)

/* ============================================================ helper umum */

const iso = () => new Date().toISOString()
const num = (value: unknown) => Number(value ?? 0)
const up = (value: unknown) =>
  String(value ?? '')
    .trim()
    .toUpperCase()
const requireAdmin = (what: string) => requireRole(`${what} hanya oleh Admin WH RM.`, ROLE.admin)
const PD_ROUTE = 'warehouse-setup-tat/pendataan-awal'
const PD_OPERATOR_ROUTE = 'warehouse-setup-tat/pendataan-awal-petugas'

const PD_TX_TABLES = [
  'initial_capture_session',
  'initial_capture_area',
  'initial_capture_scan',
  'rm_opening_stock',
  'rm_unit',
  'mt_material',
  'mt_material_group',
  'mt_unit',
  'stock_movement',
  'notification',
  'tx_log'
]

/** Semua perubahan satu aksi dibatalkan bila ada galat di tengah jalan (pola `inTransaction` Batch A). */
function inTransaction<T>(run: () => T): T {
  const snapshot = PD_TX_TABLES.map(collection => [collection, readTable(collection)] as const)
  try {
    return run()
  } catch (error) {
    snapshot.forEach(([collection, rows]) => writeTable(collection, rows))
    throw error
  }
}

/** Banyak baris sekaligus — satu tulis localStorage, bukan satu per baris (stiker bisa ratusan). */
function insertRows(collection: string, rows: LocalRow[]) {
  if (rows.length) writeTable(collection, [...readTable(collection), ...rows])
  return rows
}
function patchRows(collection: string, ids: Set<string>, patch: (row: LocalRow) => Record<string, unknown>) {
  if (!ids.size) return
  writeTable(
    collection,
    readTable(collection).map(row => (ids.has(String(row.id)) ? { ...row, ...patch(row), id: row.id } : row))
  )
}

const unitRow = (id: unknown) => findRow('mt_unit', idOf(id))
const unitNameOf = (id: unknown) => String(unitRow(id)?.name ?? '')
const findUnitByName = (name: string, kind: 'PACK' | 'SMALL') =>
  readTable('mt_unit').find(row => row.kind === kind && String(row.name).toLowerCase() === name.toLowerCase())

/** Satuan dari ukuran standar yang belum ada di Master Unit ditambahkan (prototype `pastikanSatuan`). */
function ensureUnit(name: string, kind: 'PACK' | 'SMALL') {
  const existing = findUnitByName(name, kind)
  if (existing) return existing
  const taken = new Set(readTable('mt_unit').map(row => up(row.code)))
  let code = up(name).replace(/[^A-Z0-9]+/g, '-') || 'SATUAN'
  if (taken.has(code)) code = `${code}-${kind === 'PACK' ? 'KMS' : 'KCL'}`
  const row = insertRow('mt_unit', {
    ...auditFieldsForCreate(),
    id: newId(),
    code,
    name,
    kind,
    description: 'Ditambahkan dari Pendataan Awal',
    inactive_reason: null,
    is_active: true
  })
  txLog(
    LOG_TYPE,
    'TAT-01',
    'pd-satuan',
    name,
    `Satuan ${name} (${kind === 'PACK' ? 'kemasan' : 'kecil'}) ditambahkan ke Master Unit dari Pendataan Awal`
  )
  return row
}

const materialName = (row: LocalRow | undefined) => String(row?.name ?? '')

/* ============================================================ sesi & baris */

export const pdActiveSession = () =>
  readTable('initial_capture_session').find(row =>
    ['RUNNING', 'AWAITING_APPROVAL'].includes(String(row.session_status))
  )

function requireSession(state?: 'RUNNING' | 'AWAITING_APPROVAL') {
  const session = pdActiveSession()
  if (!session) fail('Belum ada pendataan awal yang berjalan.', 'ERR_NO_SESSION', 409)
  if (state === 'RUNNING' && session.session_status !== 'RUNNING')
    fail('Opname pertama sudah ditutup dan menunggu persetujuan Supervisor WH RM.', 'ERR_WORKFLOW_STATE_MISMATCH', 422)
  if (state === 'AWAITING_APPROVAL' && session.session_status !== 'AWAITING_APPROVAL')
    fail('Opname pertama belum ditutup Admin WH RM.', 'ERR_WORKFLOW_STATE_MISMATCH', 422)
  return session
}

const linesOf = (sessionId: string) =>
  readTable('rm_opening_stock')
    .filter(row => idOf(row.session_id) === sessionId)
    .sort((a, b) => num(a.line_no ?? 1e9) - num(b.line_no ?? 1e9) || String(a.id).localeCompare(String(b.id)))

const lineOf = (id: unknown, sessionId: string) => {
  const row = findRow('rm_opening_stock', idOf(id))
  if (!row || idOf(row.session_id) !== sessionId) fail('Baris material tidak ditemukan.', 'ERR_NOT_FOUND', 404)
  return row
}

const isIncluded = (line: LocalRow) => line.resolution === 'OK' || line.resolution === 'ADDED'
const categoryOf = (line: LocalRow) => (line.category_name as PdCategory) ?? 'Lain-lain'
const stdOf = (line: LocalRow) => PD_STD[categoryOf(line)] ?? PD_STD['Lain-lain']
/** Isi per kemasan bawaan (placeholder): ukuran standar kategori (prototype `stdBaris`). */
const stdQtyOf = (line: LocalRow) => stdOf(line)[1]

export type LineStatus = 'NO_STANDARD' | 'REVIEW' | 'APPROVED'
export const lineStatusOf = (line: LocalRow): LineStatus =>
  !num(line.qty_per_pack) ? 'NO_STANDARD' : line.pack_plan ? 'APPROVED' : 'REVIEW'

const planTotal = (plan: PackPart[] | null | undefined) =>
  (plan ?? []).reduce((sum, part) => sum + part.n * part.isi, 0)
const planPacks = (plan: PackPart[] | null | undefined) => (plan ?? []).reduce((sum, part) => sum + part.n, 0)

const sessionUnits = (sessionId: string) =>
  readTable('rm_unit').filter(unit => unit.flag === 'OPENING' && idOf(unit.capture_session_id) === sessionId)
const lineUnits = (lineId: string) =>
  readTable('rm_unit').filter(
    unit => idOf(unit.opening_line_id) === lineId && unit.unit_status === PD_UNIT_STATUS.awaiting
  )

/* ------------------------------------------------------------ hasil scan */

const scansOf = (sessionId: string) =>
  readTable('initial_capture_scan').filter(row => idOf(row.session_id) === sessionId && !row.undone_at)

/** Keadaan satu kemasan di opname pertama: hasil scan terakhir yang belum diurungkan. */
function unitStateMap(sessionId: string) {
  const map = new Map<string, LocalRow>()
  scansOf(sessionId)
    .filter(row => row.unit_id)
    .sort((a, b) => String(a.at).localeCompare(String(b.at)))
    .forEach(row => map.set(idOf(row.unit_id), row))
  return map
}

/**
 * Keadaan posisi: `ada` (n kemasan aktif), `kosong` (ditandai kosong), atau tidak ada (belum dikunjungi). Posisi yang
 * pernah berisi scan tetap dihitung dikunjungi walau scannya diurungkan (prototype `tandaiPosisi`).
 */
function positionStates(sessionId: string) {
  const grouped = new Map<string, { count: number; empty: boolean }>()
  readTable('initial_capture_scan')
    .filter(row => idOf(row.session_id) === sessionId && row.rack_bin_id)
    .forEach(row => {
      const bin = idOf(row.rack_bin_id)
      const entry = grouped.get(bin) ?? { count: 0, empty: false }
      if (!row.undone_at && row.result === 'PRESENT') entry.count += 1
      if (!row.undone_at && row.result === 'EMPTY') entry.empty = true
      grouped.set(bin, entry)
    })
  const states = new Map<string, { kind: 'ada' | 'kosong'; count: number }>()
  grouped.forEach((entry, bin) =>
    states.set(bin, { kind: entry.count === 0 && entry.empty ? 'kosong' : 'ada', count: entry.count })
  )
  return states
}

/* ------------------------------------------------------------ rack */

const POSITION_CODE = /^R\d{2}-B\d{2}-L\d{2}-P\d{2}$/

/** Rack RM aktif beserta posisi aktifnya (bukan area khusus, bukan gudang satelit). */
export function pdRacks() {
  const bins = binsOfRm()
    .filter(bin => bin.is_active !== false && POSITION_CODE.test(String(bin.code)))
    .sort((a, b) => String(a.code).localeCompare(String(b.code)))
  return racksOfRm()
    .filter(rack => rack.is_active !== false)
    .map(rack => ({ rack, positions: bins.filter(bin => idOf(bin.rack_id) === rack.id) }))
    .filter(entry => entry.positions.length > 0)
    .sort((a, b) => String(a.rack.code).localeCompare(String(b.rack.code)))
}

const areaOf = (sessionId: string, rackId: string) =>
  readTable('initial_capture_area').find(row => idOf(row.session_id) === sessionId && idOf(row.rack_id) === rackId)

const isSent = (sessionId: string, rackId: string) => Boolean(areaOf(sessionId, rackId)?.submitted_at)

function rackProgress(sessionId: string) {
  const states = positionStates(sessionId)
  const units = unitStateMap(sessionId)
  return pdRacks().map(({ rack, positions }) => {
    const area = areaOf(sessionId, String(rack.id))
    const visited = positions.filter(bin => states.has(String(bin.id))).length
    const ids = new Set(positions.map(bin => String(bin.id)))
    const packages = [...units.values()].filter(
      row => row.result === 'PRESENT' && ids.has(idOf(row.rack_bin_id))
    ).length
    const sent = Boolean(area?.submitted_at)
    return {
      rack_id: String(rack.id),
      code: String(rack.code),
      name: String(rack.name ?? ''),
      operator_id: area?.operator_id ? idOf(area.operator_id) : null,
      operator_name: area?.operator_id ? userName(area.operator_id) : null,
      submitted_at: (area?.submitted_at as string | null) ?? null,
      visited,
      total: positions.length,
      packages,
      returned_at: (area?.returned_at as string | null) ?? null,
      return_note: (area?.return_note as string | null) ?? null,
      status: sent ? 'SENT' : area?.returned_at ? 'RETURNED' : visited ? 'IN_PROGRESS' : 'NOT_STARTED'
    }
  })
}

const opnameStarted = (session: LocalRow) => Boolean(session.opname_started_at)

function summaryOf(sessionId: string) {
  const units = sessionUnits(sessionId).filter(unit => unit.unit_status === PD_UNIT_STATUS.awaiting)
  const states = unitStateMap(sessionId)
  const placed = units.filter(unit => states.get(String(unit.id))?.result === 'PRESENT').length
  const lost = units.filter(unit => states.get(String(unit.id))?.result === 'LOST').length
  const racks = rackProgress(sessionId)
  return {
    total: units.length,
    placed,
    lost,
    remaining: units.length - placed - lost,
    racks: racks.length,
    racks_sent: racks.filter(rack => rack.submitted_at).length,
    racks_assigned: racks.filter(rack => rack.operator_id).length
  }
}

const allSent = (sessionId: string) => {
  const racks = rackProgress(sessionId)
  return racks.length > 0 && racks.every(rack => rack.submitted_at)
}

/* ============================================================ GET state (TAT-01) */

function lineView(line: LocalRow, locked: Set<string>) {
  const material = line.material_id ? findRow('mt_material', idOf(line.material_id)) : undefined
  const packName = unitNameOf(line.unit_id) || stdOf(line)[0]
  const smallName = unitNameOf(line.small_unit_id) || stdOf(line)[2]
  const status = lineStatusOf(line)
  const plan = (line.pack_plan as PackPart[] | null) ?? null
  const printed = lineUnits(String(line.id)).length
  const proposal = labelProposal(num(line.qty), num(line.qty_per_pack) || 0, packName, smallName)
  return {
    id: String(line.id),
    line_no: (line.line_no as number | null) ?? null,
    source_name: String(line.source_name ?? ''),
    category: categoryOf(line),
    qty: num(line.qty),
    qty_source: num(line.qty_source),
    material: material ? { id: String(material.id), code: String(material.code), name: materialName(material) } : null,
    match_status: String(line.match_status),
    resolution: (line.resolution as string | null) ?? null,
    included: isIncluded(line),
    unit_id: line.unit_id ? idOf(line.unit_id) : null,
    unit_name: packName,
    small_unit_id: line.small_unit_id ? idOf(line.small_unit_id) : null,
    small_unit_name: smallName,
    qty_per_pack: num(line.qty_per_pack) || null,
    std_qty_per_pack: stdQtyOf(line),
    status,
    proposal: proposal ? { text: proposal.text, count: proposal.count } : null,
    pack_plan: plan,
    label_count: planPacks(plan),
    printed,
    needs_reprint: Boolean(line.needs_reprint),
    printed_at: (line.printed_at as string | null) ?? null,
    saldo_correction: (line.saldo_correction as Record<string, unknown> | null) ?? null,
    is_manual: Boolean(line.is_manual),
    locked: locked.has(String(line.id))
  }
}

function sessionView(session: LocalRow) {
  return {
    id: String(session.id),
    pdw_no: String(session.pdw_no),
    session_status: String(session.session_status),
    import_meta: (session.import_meta as Record<string, unknown> | null) ?? null,
    created_at: String(session.date_created ?? ''),
    created_by: userName(session.user_created),
    opname_started_at: (session.opname_started_at as string | null) ?? null,
    opname_started_by: session.opname_started_by ? userName(session.opname_started_by) : null,
    closed_at: (session.closed_at as string | null) ?? null,
    closed_by: session.closed_by ? userName(session.closed_by) : null,
    approved_at: (session.approved_at as string | null) ?? null,
    approved_by: session.approved_by ? userName(session.approved_by) : null,
    return_note: (session.return_note as string | null) ?? null,
    returned_by: session.returned_by ? userName(session.returned_by) : null,
    returned_at: (session.returned_at as string | null) ?? null,
    result: (session.result as Record<string, number> | null) ?? null
  }
}

const operatorsRm = () =>
  readTable('mt_privileges')
    .filter(row => String(findRow('mt_roles', idOf(row.role))?.code) === ROLE.operator)
    .map(row => ({ id: idOf(row.user), name: userName(row.user) }))
    .filter((row, index, all) => all.findIndex(other => other.id === row.id) === index)

export function pdState() {
  const session = pdActiveSession()
  const history = readTable('initial_capture_session')
    .filter(row => row.session_status === 'COMPLETED')
    .map(row => ({ ...sessionView(row), packages: sessionUnits(String(row.id)).length }))
  const options = {
    categories: PD_CATEGORIES.map(category => ({
      value: category,
      pack: PD_STD[category][0],
      qty: PD_STD[category][1],
      small: PD_STD[category][2]
    })),
    saldo_reasons: [...PD_SALDO_REASONS],
    lost_reasons: [...PD_LOST_REASONS],
    reprint_reasons: [...PD_REPRINT_REASONS],
    operators: operatorsRm()
  }
  if (!session) return { session: null, lines: [], racks: [], summary: null, steps: null, history, options }
  const sessionId = String(session.id)
  const states = unitStateMap(sessionId)
  const locked = new Set(
    sessionUnits(sessionId)
      .filter(unit => unit.unit_status === PD_UNIT_STATUS.awaiting && states.has(String(unit.id)))
      .map(unit => idOf(unit.opening_line_id))
  )
  const lines = linesOf(sessionId).map(line => lineView(line, locked))
  const included = lines.filter(line => line.included)
  const approved = included.filter(line => line.status === 'APPROVED')
  const summary = summaryOf(sessionId)
  return {
    session: sessionView(session),
    lines,
    racks: rackProgress(sessionId),
    summary,
    steps: {
      materials_total: included.length,
      materials_approved: approved.length,
      new_unresolved: lines.filter(line => line.match_status === 'NEW' && !line.resolution).length,
      skipped: lines.filter(line => line.resolution === 'SKIPPED').length,
      stickers_total: approved.reduce((sum, line) => sum + line.label_count, 0),
      stickers_printed: approved.reduce((sum, line) => sum + (line.needs_reprint ? 0 : line.printed), 0),
      opname_started: opnameStarted(session),
      touched: summary.placed + summary.lost > 0 || positionStates(sessionId).size > 0
    },
    history,
    options
  }
}

/** Daftar kemasan sesi (kartu "Kemasan" TAT-01): kode, material, isi, keadaan opname. */
export function pdUnits() {
  const session = pdActiveSession()
  if (!session) return { units: [] }
  const sessionId = String(session.id)
  const states = unitStateMap(sessionId)
  const lines = new Map(linesOf(sessionId).map(line => [String(line.id), line]))
  const units = sessionUnits(sessionId)
    .filter(unit => unit.unit_status === PD_UNIT_STATUS.awaiting)
    .sort((a, b) => String(a.qr_code).localeCompare(String(b.qr_code)))
    .map(unit => {
      const state = states.get(String(unit.id))
      const material = findRow('mt_material', idOf(unit.material_id))
      const line = lines.get(idOf(unit.opening_line_id))
      return {
        id: String(unit.id),
        qr_code: String(unit.qr_code),
        number: stickerNumber(String(unit.qr_code)),
        material_code: String(material?.code ?? ''),
        material_name: materialName(material) || String(line?.source_name ?? ''),
        qty: num(unit.qty_small),
        small_unit: line ? unitNameOf(line.small_unit_id) || stdOf(line)[2] : '',
        state: (state?.result as 'PRESENT' | 'LOST' | undefined) ?? null,
        position: state?.rack_bin_id ? String(findRow('mt_rack_bin', idOf(state.rack_bin_id))?.code ?? '') : null,
        by_name: state ? userName(state.by_id) : null,
        at: (state?.at as string | null) ?? null,
        reasons: (state?.reasons as string[] | null) ?? null
      }
    })
  return { units }
}

/* ============================================================ impor */

type ImportItem = { no?: number | null; name?: string; saldo?: unknown }
type ImportBody = {
  items?: ImportItem[]
  file_name?: string
  sheet?: string
  skipped_zero?: number
  replace?: boolean
}

function masterIndex() {
  const map = new Map<string, LocalRow>()
  readTable('mt_material')
    .filter(row => row.is_active !== false)
    .forEach(row => {
      const key = normName(row.name)
      if (key && !map.has(key)) map.set(key, row)
    })
  return map
}

/** Pratinjau impor: kecocokan dengan master, nama ganda, saldo yang ditolak (bukan angka / desimal). */
export function importPreview(body: ImportBody) {
  requireAdmin('Impor saldo pendataan awal')
  const index = masterIndex()
  const items: { no: number | null; name: string; saldo: number; in_master: boolean }[] = []
  const invalid: { no: number | null; name: string; value: string; reason: string }[] = []
  let zero = 0
  const seen = new Set<string>()
  let duplicates = 0
  ;(body.items ?? []).forEach(item => {
    const name = String(item.name ?? '')
      .replace(/\s+/g, ' ')
      .trim()
    if (!name) return
    const saldo = parseSaldo(item.saldo)
    if (saldo.kind === 'zero') return void (zero += 1)
    if (saldo.kind !== 'ok') {
      invalid.push({
        no: item.no ?? null,
        name,
        value: String(item.saldo ?? ''),
        reason: saldo.kind === 'decimal' ? 'saldo harus bilangan bulat' : 'saldo bukan angka'
      })
      return
    }
    const key = normName(name)
    if (seen.has(key)) duplicates += 1
    seen.add(key)
    items.push({ no: item.no ?? null, name, saldo: Number(saldo.value), in_master: index.has(key) })
  })
  return {
    items,
    invalid,
    skipped_zero: zero + num(body.skipped_zero),
    new_count: items.filter(item => !item.in_master).length,
    duplicate_count: duplicates
  }
}

/** Ganti berkas: sesi lama dibatalkan dan stiker yang belum dipakai tidak berlaku lagi. */
function cancelSessionForReplace(session: LocalRow) {
  const sessionId = String(session.id)
  if (positionStates(sessionId).size || scansOf(sessionId).length)
    fail('Opname pertama sudah berjalan — berkas Excel tidak bisa diganti lagi.', 'ERR_WORKFLOW_STATE_MISMATCH', 422)
  const active = sessionUnits(sessionId).filter(unit => unit.unit_status === PD_UNIT_STATUS.awaiting)
  const cancelled = active.length
  patchRows('rm_unit', new Set(active.map(unit => String(unit.id))), () => ({
    unit_status: PD_UNIT_STATUS.cancelled,
    ...auditFieldsForUpdate()
  }))
  updateRow('initial_capture_session', sessionId, {
    session_status: 'CANCELLED',
    cancelled_at: iso(),
    cancel_reason: 'Berkas Excel diganti',
    ...auditFieldsForUpdate()
  })
  txLog(
    LOG_TYPE,
    'TAT-01',
    'pd-ganti',
    String(session.pdw_no),
    `Berkas ${String((session.import_meta as { file_name?: string } | null)?.file_name ?? '—')} diganti: ${linesOf(sessionId).length} baris pemetaan dan ${cancelled} stiker dibuang`
  )
}

export function importCommit(body: ImportBody) {
  requireAdmin('Impor saldo pendataan awal')
  const fileName = String(body.file_name ?? '').trim()
  if (!fileName) fail('Nama berkas tidak ada.')
  const raw = body.items ?? []
  const bad = raw.find(item => parseSaldo(item.saldo).kind !== 'ok')
  if (bad) {
    const kind = parseSaldo(bad.saldo).kind
    fail(
      kind === 'decimal'
        ? `Saldo ${String(bad.name ?? '')} (${String(bad.saldo)}) bukan bilangan bulat — saldo pendataan awal selalu bulat.`
        : `Saldo ${String(bad.name ?? '')} (${String(bad.saldo ?? '')}) bukan angka lebih dari nol.`,
      kind === 'decimal' ? 'ERR_DECIMAL_SALDO' : 'ERR_VALIDATION_FAILED'
    )
  }
  const items = raw
    .map(item => ({
      no: item.no ?? null,
      name: String(item.name ?? '')
        .replace(/\s+/g, ' ')
        .trim(),
      saldo: Number(parseSaldo(item.saldo).value)
    }))
    .filter(item => item.name)
  if (!items.length) fail('Tidak ada material bersaldo di berkas ini.')
  return inTransaction(() => {
    const current = pdActiveSession()
    if (current) {
      if (!body.replace) fail('Masih ada pendataan awal yang berjalan.', 'ERR_SESSION_OPEN', 409)
      if (current.session_status !== 'RUNNING' || opnameStarted(current))
        fail('Opname pertama sudah dimulai — berkas Excel tidak bisa diganti lagi.', 'ERR_WORKFLOW_STATE_MISMATCH', 422)
      cancelSessionForReplace(current)
    } else if (readTable('initial_capture_session').some(row => row.session_status === 'COMPLETED')) {
      fail(
        'Pendataan awal sudah selesai dan disetujui — saldo pembukaan tidak diimpor dua kali.',
        'ERR_SESSION_DONE',
        409
      )
    }
    const index = masterIndex()
    const sessionId = newId()
    const pdwNo = nextDocNo('PDW', 'initial_capture_session', 'pdw_no')
    insertRow('initial_capture_session', {
      ...auditFieldsForCreate(),
      id: sessionId,
      pdw_no: pdwNo,
      session_status: 'RUNNING',
      warehouse_id: readTable('mt_warehouses').find(row => row.kind !== 'SGI')?.id ?? null,
      import_meta: {
        file_name: fileName,
        sheet: String(body.sheet ?? ''),
        rows_read: items.length,
        rows_skipped: num(body.skipped_zero)
      },
      opname_started_at: null,
      opname_started_by: null,
      closed_at: null,
      closed_by: null,
      approved_at: null,
      approved_by: null,
      return_note: null,
      returned_by: null,
      returned_at: null,
      result: null
    })
    insertRows(
      'rm_opening_stock',
      items.map(item => {
        const material = index.get(normName(item.name))
        const category = material ? categoryFromMaster(material, item.name) : pdCategoryOf(item.name)
        return {
          ...auditFieldsForCreate(),
          id: newId(),
          session_id: sessionId,
          ...newLineFields({ no: item.no, name: item.name, saldo: item.saldo, category, material })
        }
      })
    )
    ensureProposedCodes(sessionId)
    const fresh = items.filter(item => !index.has(normName(item.name))).length
    txLog(
      LOG_TYPE,
      'TAT-01',
      'pd-impor',
      pdwNo,
      `Impor ${fileName}${body.sheet ? ` (lembar ${body.sheet})` : ''}: ${items.length} material bersaldo, ${num(body.skipped_zero)} baris saldo 0 dilewati, ${fresh} belum ada di master`
    )
    return { session_id: sessionId, pdw_no: pdwNo, lines: items.length, new_count: fresh }
  })
}

/** Kategori Pendataan Awal untuk material yang sudah ada di master (prototype `DARI_MASTER`). */
function categoryFromMaster(material: LocalRow, name: string): PdCategory {
  const group = String(findRow('mt_material_group', idOf(material.category_id))?.name ?? '')
  const key = group.toLowerCase()
  if (PD_FROM_MASTER[key]) return PD_FROM_MASTER[key]
  const direct = PD_CATEGORIES.find(category => category.toLowerCase() === key)
  return direct ?? pdCategoryOf(name)
}

export function newLineFields(args: {
  no: number | null
  name: string
  saldo: number
  category: PdCategory
  material?: LocalRow
  manual?: boolean
}) {
  const { material, category } = args
  return {
    line_no: args.no,
    source_name: args.name,
    category_name: category,
    qty_source: args.saldo,
    qty: args.saldo,
    material_id: material?.id ?? null,
    match_status: material ? 'MATCHED' : 'NEW',
    resolution: material ? 'OK' : null,
    unit_id: material?.default_purchase_unit_id ?? findUnitByName(PD_STD[category][0], 'PACK')?.id ?? null,
    small_unit_id: material?.unit_small_id ?? findUnitByName(PD_STD[category][2], 'SMALL')?.id ?? null,
    qty_per_pack: null,
    pack_plan: null,
    line_status: 'NO_STANDARD',
    printed_at: null,
    needs_reprint: false,
    batch_no: null,
    saldo_correction: null,
    is_manual: Boolean(args.manual)
  }
}

/* ============================================================ pemetaan kemasan */

function requireEditableLine(line: LocalRow, sessionId: string) {
  if (!isIncluded(line)) fail('Material ini belum ada di master atau dilewati.', 'ERR_WORKFLOW_STATE_MISMATCH', 422)
  const states = unitStateMap(sessionId)
  if (lineUnits(String(line.id)).some(unit => states.has(String(unit.id))))
    fail('Kemasan material ini sudah discan di opname pertama — pemetaannya terkunci.', 'ERR_LINE_LOCKED', 409)
}

/** Mengubah kemasan / isi / saldo membatalkan persetujuan; stiker yang sudah tercetak ikut dibatalkan. */
function cancelApproval(line: LocalRow, reason: string) {
  const units = lineUnits(String(line.id))
  if (!line.pack_plan && !units.length) return false
  patchRows('rm_unit', new Set(units.map(unit => String(unit.id))), () => ({
    unit_status: PD_UNIT_STATUS.cancelled,
    ...auditFieldsForUpdate()
  }))
  updateRow('rm_opening_stock', String(line.id), {
    pack_plan: null,
    needs_reprint: units.length > 0 || Boolean(line.needs_reprint),
    ...auditFieldsForUpdate()
  })
  if (units.length)
    txLog(
      LOG_TYPE,
      'TAT-01',
      'pd-stiker-batal',
      String(line.source_name),
      `${units.length} stiker ${String(line.source_name)} dibatalkan — ${reason}`
    )
  txLog(
    LOG_TYPE,
    'TAT-01',
    'pd-batal-setuju',
    String(line.source_name),
    `${String(line.source_name)}: persetujuan dibatalkan — ${reason}`
  )
  return true
}

const syncStatus = (lineId: string) => {
  const line = findRow('rm_opening_stock', lineId)
  if (line) updateRow('rm_opening_stock', lineId, { line_status: lineStatusOf(line) })
}

function integerOrFail(value: unknown, label: string) {
  const parsed = parseSaldo(value)
  if (parsed.kind === 'decimal') fail(`${label} harus bilangan bulat (tanpa desimal).`, 'ERR_DECIMAL_SALDO')
  if (parsed.kind !== 'ok') fail(`${label} harus angka bulat lebih dari nol.`)
  return Number(parsed.value)
}

type PackBody = { line_id?: string; unit_id?: string | null; qty_per_pack?: unknown }

function applyPack(line: LocalRow, sessionId: string, unitId: string | null | undefined, qtyRaw: unknown) {
  requireEditableLine(line, sessionId)
  const patch: Record<string, unknown> = {}
  if (unitId !== undefined && unitId !== null && unitId !== '') {
    const unit = unitRow(unitId)
    if (!unit || unit.kind !== 'PACK') fail('Kemasan harus dipilih dari Master Unit (kategori kemasan).')
    if (idOf(line.unit_id) !== String(unit.id)) patch.unit_id = unit.id
  }
  if (qtyRaw !== undefined && qtyRaw !== null && qtyRaw !== '') {
    const qty = integerOrFail(qtyRaw, 'Isi per kemasan')
    if (num(line.qty_per_pack) !== qty) patch.qty_per_pack = qty
  } else if (qtyRaw === null || qtyRaw === '') {
    if (line.qty_per_pack) patch.qty_per_pack = null
  }
  if (!Object.keys(patch).length) return false
  updateRow('rm_opening_stock', String(line.id), { ...patch, ...auditFieldsForUpdate() })
  cancelApproval(
    findRow('rm_opening_stock', String(line.id)) as LocalRow,
    patch.unit_id ? 'kemasan diubah' : 'isi per kemasan diubah'
  )
  syncStatus(String(line.id))
  const after = findRow('rm_opening_stock', String(line.id)) as LocalRow
  const proposal = labelProposal(
    num(after.qty),
    num(after.qty_per_pack),
    unitNameOf(after.unit_id),
    unitNameOf(after.small_unit_id)
  )
  txLog(
    LOG_TYPE,
    'TAT-01',
    'pd-isi',
    String(after.source_name),
    `${String(after.source_name)}: ${unitNameOf(after.unit_id)} isi ${num(after.qty_per_pack) || '—'} ${unitNameOf(after.small_unit_id)}${proposal ? `, usulan ${proposal.text}` : ''}`
  )
  return true
}

export function lineSetPack(body: PackBody) {
  requireAdmin('Pemetaan kemasan')
  const session = requireSession('RUNNING')
  const line = lineOf(body.line_id, String(session.id))
  return inTransaction(() => ({ changed: applyPack(line, String(session.id), body.unit_id, body.qty_per_pack) }))
}

export function lineApplyBulk(body: { line_ids?: string[]; unit_id?: string | null; qty_per_pack?: unknown }) {
  requireAdmin('Pemetaan kemasan')
  const session = requireSession('RUNNING')
  const sessionId = String(session.id)
  const ids = [...new Set(body.line_ids ?? [])]
  if (!ids.length) fail('Centang dulu material yang akan diisi.')
  if (!body.unit_id && (body.qty_per_pack === undefined || body.qty_per_pack === null || body.qty_per_pack === ''))
    fail('Isi kemasan atau isi per kemasan dulu.')
  const lines = ids.map(id => lineOf(id, sessionId))
  const smallUnits = new Set(lines.map(line => idOf(line.small_unit_id) || stdOf(line)[2]))
  if (smallUnits.size > 1)
    fail('Satuan isi material yang dicentang berbeda — terapkan massal butuh satuan yang sama.', 'ERR_MIXED_UNIT', 422)
  return inTransaction(() => {
    let changed = 0
    lines.forEach(line => {
      if (applyPack(line, sessionId, body.unit_id ?? undefined, body.qty_per_pack)) changed += 1
    })
    return { applied: lines.length, changed, unchanged: lines.length - changed }
  })
}

export function lineApproveBulk(body: { line_ids?: string[] }) {
  requireAdmin('Persetujuan pemetaan')
  const session = requireSession('RUNNING')
  const sessionId = String(session.id)
  const lines = [...new Set(body.line_ids ?? [])].map(id => lineOf(id, sessionId))
  const ready = lines.filter(line => isIncluded(line) && lineStatusOf(line) === 'REVIEW' && num(line.qty) > 0)
  if (!ready.length) fail('Tidak ada material berstatus Perlu review di antara yang dicentang.')
  return inTransaction(() => {
    let labels = 0
    ready.forEach(line => {
      const proposal = labelProposal(num(line.qty), num(line.qty_per_pack)) as NonNullable<
        ReturnType<typeof labelProposal>
      >
      labels += proposal.count
      updateRow('rm_opening_stock', String(line.id), {
        pack_plan: proposal.plan,
        line_status: 'APPROVED',
        ...auditFieldsForUpdate()
      })
      txLog(
        LOG_TYPE,
        'TAT-01',
        'pd-petakan',
        String(line.source_name),
        `${String(line.source_name)} disetujui: ${proposal.plan.map(part => `${part.n} × ${part.isi}`).join(' + ')} ${unitNameOf(line.small_unit_id)} = ${planTotal(proposal.plan)} (saldo ${num(line.qty)})`
      )
    })
    return { approved: ready.length, skipped: lines.length - ready.length, labels }
  })
}

export function lineSetSaldo(body: { line_id?: string; qty?: unknown; reasons?: string[] }) {
  requireAdmin('Koreksi saldo')
  const session = requireSession('RUNNING')
  const sessionId = String(session.id)
  const line = lineOf(body.line_id, sessionId)
  requireEditableLine(line, sessionId)
  const qty = integerOrFail(body.qty, 'Saldo')
  const reasons = (body.reasons ?? []).map(reason => String(reason).trim()).filter(Boolean)
  if (!reasons.length) fail('Alasan koreksi saldo wajib dipilih.', 'ERR_REASON_REQUIRED')
  if (reasons.some(reason => !(PD_SALDO_REASONS as readonly string[]).includes(reason)))
    fail('Alasan tidak dikenal.', 'ERR_VALIDATION_FAILED')
  if (qty === num(line.qty)) return { changed: false }
  return inTransaction(() => {
    const from = num(line.qty)
    updateRow('rm_opening_stock', String(line.id), {
      qty,
      saldo_correction:
        qty === num(line.qty_source)
          ? null
          : { from: num(line.qty_source), to: qty, reasons, by: currentUserId(), at: iso() },
      ...auditFieldsForUpdate()
    })
    cancelApproval(findRow('rm_opening_stock', String(line.id)) as LocalRow, 'saldo dikoreksi')
    syncStatus(String(line.id))
    txLog(
      LOG_TYPE,
      'TAT-01',
      'pd-saldo',
      String(line.source_name),
      `${String(line.source_name)}: saldo ${fmtId(from)} → ${fmtId(qty)} (Excel ${fmtId(num(line.qty_source))}), alasan: ${reasons.join(', ')}`
    )
    return { changed: true }
  })
}

export function lineSkip(body: { line_ids?: string[]; undo?: boolean }) {
  requireAdmin('Melewati material')
  const session = requireSession('RUNNING')
  const sessionId = String(session.id)
  const lines = [...new Set(body.line_ids ?? [])].map(id => lineOf(id, sessionId))
  const target = lines.filter(line =>
    body.undo ? line.resolution === 'SKIPPED' : line.match_status === 'NEW' && !line.resolution
  )
  if (!target.length) fail('Tidak ada material belum-ada-di-master yang dipilih.')
  target.forEach(line =>
    updateRow('rm_opening_stock', String(line.id), {
      resolution: body.undo ? null : 'SKIPPED',
      ...auditFieldsForUpdate()
    })
  )
  txLog(
    LOG_TYPE,
    'TAT-01',
    body.undo ? 'pd-urung-lewati' : 'pd-lewati',
    String(session.pdw_no),
    body.undo
      ? `${target.length} material dikembalikan ke daftar belum ada di master`
      : `${target.length} material belum ada di master dilewati`
  )
  return { count: target.length }
}

/* ------------------------------------------------------------ tambah ke master */

type MasterBody = {
  line_id?: string
  name?: string
  code?: string
  category?: string
  unit_id?: string
  unit_name?: string
  small_unit_id?: string
  small_unit_name?: string
  qty_per_pack?: unknown
  qty?: unknown
}

function groupOf(category: PdCategory) {
  const name = PD_TO_MASTER[category] ?? category
  const existing = readTable('mt_material_group').find(row => String(row.name).toLowerCase() === name.toLowerCase())
  if (existing) return existing
  return insertRow('mt_material_group', {
    ...auditFieldsForCreate(),
    id: newId(),
    code: up(name).replace(/[^A-Z0-9]+/g, '-'),
    name,
    is_active: true
  })
}

/** Kemasan / satuan dari id Master Unit, atau nama ukuran standar yang ditambahkan ke Master Unit. */
function resolveUnit(id: string | undefined, name: string | undefined, kind: 'PACK' | 'SMALL', label: string) {
  if (id) {
    const row = unitRow(id)
    if (!row || row.kind !== kind) fail(`${label} harus dipilih dari Master Unit.`)
    return row
  }
  if (name?.trim()) return ensureUnit(name.trim(), kind)
  return fail(`${label} wajib dipilih.`)
}

function createMaterial(args: {
  name: string
  code: string
  category: PdCategory
  pack: LocalRow
  small: LocalRow
  perPack: number
}) {
  const material = insertRow('mt_material', {
    ...auditFieldsForCreate(),
    id: newId(),
    code: args.code,
    name: args.name,
    category_id: groupOf(args.category).id,
    unit_small_id: args.small.id,
    cap_day: null,
    rop_manual: null,
    variant: null,
    default_purchase_unit_id: args.pack.id,
    default_purchase_unit_qty: args.perPack,
    inactive_reason: null,
    is_active: true
  })
  txLog(
    LOG_TYPE,
    'TAT-01',
    'pd-master',
    args.code,
    `Material ${args.code} — ${args.name} ditambahkan ke Master Material (${String(args.pack.name)} isi ${args.perPack} ${String(args.small.name)}); supplier diisi di Master Raw Material`
  )
  return material
}

/* ------------------------------------------------ kode material (keputusan 2026-10-21 #8) */

const pendingNewLines = (sessionId: string) =>
  linesOf(sessionId).filter(line => line.match_status === 'NEW' && !line.resolution)

/**
 * Setiap baris "belum ada di master" memesan satu usulan kode (`rm_opening_stock.proposed_code`, ERD-ADD laporan 32),
 * unik di seluruh impor; nama ganda di berkas berbagi satu kode (material yang sama). Dipanggil saat impor dan sebelum
 * kode dibaca (baris lama tanpa usulan ikut terisi).
 */
function ensureProposedCodes(sessionId: string) {
  const lines = pendingNewLines(sessionId)
  const taken = new Set(lines.map(line => String(line.proposed_code ?? '')).filter(Boolean))
  const byName = new Map<string, string>()
  lines.forEach(line => {
    if (line.proposed_code) byName.set(normName(line.source_name), String(line.proposed_code))
  })
  const patches = new Map<string, string>()
  lines
    .filter(line => !line.proposed_code)
    .forEach(line => {
      const name = normName(line.source_name)
      const code = byName.get(name) ?? proposeMaterialCode(categoryOf(line), taken)
      taken.add(code)
      byName.set(name, code)
      patches.set(String(line.id), code)
    })
  patchRows('rm_opening_stock', new Set(patches.keys()), row => ({ proposed_code: patches.get(String(row.id)) }))
}

/** Kode yang dipesan baris lain di impor ini (kode → nama), tanpa baris `exceptLine` dan kembarannya (nama sama). */
function reservedCodes(sessionId: string, exceptLine?: LocalRow) {
  const except = exceptLine ? normName(exceptLine.source_name) : null
  const map = new Map<string, string>()
  pendingNewLines(sessionId)
    .filter(line => line.proposed_code && normName(line.source_name) !== except)
    .forEach(line => map.set(String(line.proposed_code), String(line.source_name)))
  return map
}

/** Usulan kode untuk modal "Tambah ke master" (boleh diubah pengguna sebelum disimpan). */
export function proposeCode(params: Record<string, unknown> | undefined) {
  const category = String(params?.category ?? 'Lain-lain') as PdCategory
  const session = pdActiveSession()
  const sessionId = session ? String(session.id) : ''
  if (sessionId) ensureProposedCodes(sessionId)
  const line = params?.line_id && sessionId ? lineOf(String(params.line_id), sessionId) : undefined
  if (line?.proposed_code && categoryOf(line) === category) return { code: String(line.proposed_code) }
  const reserved = sessionId ? reservedCodes(sessionId, line) : new Map<string, string>()
  return {
    code: proposeMaterialCode(PD_CATEGORIES.includes(category) ? category : 'Lain-lain', new Set(reserved.keys()))
  }
}

/** Pratinjau kode yang diketik pengguna: pesan galat (pola, master, baris lain di impor ini) atau null. */
export function checkCode(params: Record<string, unknown> | undefined) {
  const code = normalizeMaterialCode(params?.code)
  const session = pdActiveSession()
  if (!session) return { code, problem: materialCodeProblem(code) }
  const sessionId = String(session.id)
  ensureProposedCodes(sessionId)
  const line = params?.line_id ? lineOf(String(params.line_id), sessionId) : undefined
  return { code, problem: materialCodeProblem(code, reservedCodes(sessionId, line)) }
}

/**
 * Tambah satu material ke Master Material — dari baris Excel yang belum ada di master, atau "Material baru" (baris
 * manual dengan saldo). Supplier dari Excel TIDAK diimpor (P-09): supplier diisi di Master Raw Material.
 */
export function addToMaster(body: MasterBody) {
  requireAdmin('Menambah material ke master')
  const session = requireSession('RUNNING')
  const sessionId = String(session.id)
  const line = body.line_id ? lineOf(body.line_id, sessionId) : undefined
  if (line && (line.match_status !== 'NEW' || line.resolution))
    fail('Baris ini sudah ada di master atau dilewati.', 'ERR_WORKFLOW_STATE_MISMATCH', 422)
  const name = String(body.name ?? line?.source_name ?? '')
    .replace(/\s+/g, ' ')
    .trim()
  if (!name) fail('Nama material wajib diisi.')
  if (name.length > 150) fail('Nama material maksimal 150 karakter.')
  if (readTable('mt_material').some(row => normName(row.name) === normName(name)))
    fail(`Material bernama ${name} sudah ada di master.`, 'ERR_DUPLICATE')
  const code = normalizeMaterialCode(body.code)
  ensureProposedCodes(sessionId)
  const problem = materialCodeProblem(code, reservedCodes(sessionId, line))
  if (problem) fail(problem, 'ERR_MATERIAL_CODE')
  const category = (PD_CATEGORIES.includes(body.category as PdCategory) ? body.category : 'Lain-lain') as PdCategory
  const perPack = integerOrFail(body.qty_per_pack, 'Isi standar per kemasan')
  const qty = line ? num(line.qty) : integerOrFail(body.qty, 'Saldo awal')
  return inTransaction(() => {
    const pack = resolveUnit(body.unit_id, body.unit_name, 'PACK', 'Kemasan')
    const small = resolveUnit(body.small_unit_id, body.small_unit_name, 'SMALL', 'Satuan isi')
    const material = createMaterial({ name, code, category, pack, small, perPack })
    const patch = {
      material_id: material.id,
      resolution: 'ADDED',
      proposed_code: code,
      category_name: category,
      unit_id: pack.id,
      small_unit_id: small.id,
      ...auditFieldsForUpdate()
    }
    let lineId: string
    if (line) {
      updateRow('rm_opening_stock', String(line.id), { ...patch, source_name: line.source_name })
      lineId = String(line.id)
    } else {
      lineId = newId()
      insertRow('rm_opening_stock', {
        ...auditFieldsForCreate(),
        id: lineId,
        session_id: sessionId,
        ...newLineFields({ no: null, name, saldo: qty, category, material, manual: true }),
        ...patch
      })
    }
    return { material_id: String(material.id), code: String(material.code), line_id: lineId }
  })
}

/** "Tambah semuanya ke master": kategori & ukuran standar usulan, kode diusulkan otomatis. */
export function addAllToMaster(body: { line_ids?: string[] }) {
  requireAdmin('Menambah material ke master')
  const session = requireSession('RUNNING')
  const sessionId = String(session.id)
  const all = linesOf(sessionId).filter(line => line.match_status === 'NEW' && !line.resolution)
  const wanted = body.line_ids?.length ? all.filter(line => body.line_ids?.includes(String(line.id))) : all
  if (!wanted.length) fail('Tidak ada material yang belum ada di master.')
  ensureProposedCodes(sessionId)
  return inTransaction(() => {
    const taken = new Set<string>(reservedCodes(sessionId).keys())
    const added: string[] = []
    let linked = 0
    wanted.forEach(line => {
      /* Nama ganda di berkas: baris berikutnya memakai material yang baru ditambahkan (tidak dibuat dua kali). */
      const twin = readTable('mt_material').find(row => normName(row.name) === normName(line.source_name))
      if (twin) {
        updateRow('rm_opening_stock', String(line.id), {
          material_id: twin.id,
          match_status: 'MATCHED',
          resolution: 'OK',
          unit_id: twin.default_purchase_unit_id ?? line.unit_id,
          small_unit_id: twin.unit_small_id ?? line.small_unit_id,
          ...auditFieldsForUpdate()
        })
        linked += 1
        return
      }
      const category = categoryOf(line)
      const [packName, perPack, smallName] = PD_STD[category]
      /* Kode yang dipesan baris ini saat impor; diusulkan ulang hanya bila sudah tidak sah. */
      const own = String(line.proposed_code ?? '')
      const ownOk = own && !materialCodeProblem(own)
      const code = ownOk ? own : proposeMaterialCode(category, taken)
      taken.add(code)
      const pack = ensureUnit(packName, 'PACK')
      const small = ensureUnit(smallName, 'SMALL')
      const material = createMaterial({ name: String(line.source_name), code, category, pack, small, perPack })
      updateRow('rm_opening_stock', String(line.id), {
        material_id: material.id,
        resolution: 'ADDED',
        unit_id: pack.id,
        small_unit_id: small.id,
        ...auditFieldsForUpdate()
      })
      added.push(code)
    })
    return { added: added.length, linked, codes: added }
  })
}

/* ============================================================ stiker */

const pad2 = (value: number) => pad(value, 2)
const ymd = (date: Date) => `${date.getFullYear()}${pad2(date.getMonth() + 1)}${pad2(date.getDate())}`

/** `ETK-BOLD12:20260818:01:0007` → `ETK-BOLD12-260818-01-0007` (prototype `C.stikerNo`). */
export function stickerNumber(qr: string) {
  const parts = qr.split(':')
  return parts.length === 4 ? `${parts[0]}-${parts[1].slice(2)}-${parts[2]}-${parts[3]}` : qr
}

/** Satu batch per material per hari (`KODE-YYMMDD-NN`); nomor unit berlanjut dalam batch itu. */
function nextBatch(materialCode: string, day: string) {
  const prefix = `${materialCode}:${day}:`
  const used = readTable('rm_unit')
    .map(unit => String(unit.qr_code ?? ''))
    .filter(code => code.startsWith(prefix))
    .map(code => Number(code.slice(prefix.length).split(':')[0]))
  return Math.max(0, ...used) + 1
}

const BULAN = ['Jan', 'Feb', 'Mar', 'Apr', 'Mei', 'Jun', 'Jul', 'Agu', 'Sep', 'Okt', 'Nov', 'Des']
const dateText = (date: Date) => `${date.getDate()} ${BULAN[date.getMonth()]} ${date.getFullYear()}`

export type PdLabel = { code: string; number: string; title: string; meta: string[]; quantity: string }

function labelOf(unit: LocalRow, line: LocalRow): PdLabel {
  const material = findRow('mt_material', idOf(unit.material_id))
  const created = new Date(String(unit.date_created ?? iso()))
  return {
    code: String(unit.qr_code),
    number: stickerNumber(String(unit.qr_code)),
    title: materialName(material) || String(line.source_name),
    meta: [
      String(findRow('mt_material_group', idOf(material?.category_id))?.name ?? categoryOf(line)),
      `Pendataan Awal ${dateText(created)}`
    ],
    quantity: `Isi ${fmtId(num(unit.qty_small))} ${unitNameOf(line.small_unit_id)}`
  }
}

function createStickers(line: LocalRow, session: LocalRow) {
  const material = findRow('mt_material', idOf(line.material_id)) as LocalRow
  const plan = (line.pack_plan as PackPart[]) ?? []
  const contents = plan.flatMap(part => Array.from({ length: part.n }, () => part.isi))
  const now = new Date()
  const day = ymd(now)
  const batchSeq = nextBatch(String(material.code), day)
  const batchNo = `${String(material.code)}-${day.slice(2)}-${pad2(batchSeq)}`
  const nextUid =
    Math.max(0, ...readTable('rm_unit').map(unit => Number(/(\d+)$/.exec(String(unit.uid))?.[1] ?? 0))) + 1
  const packName = unitNameOf(line.unit_id)
  return insertRows(
    'rm_unit',
    contents.map((isi, index) => ({
      ...auditFieldsForCreate(),
      id: newId(),
      uid: `UNIT-${pad(nextUid + index, 6)}`,
      qr_code: `${String(material.code)}:${day}:${pad2(batchSeq)}:${pad(index + 1, 4)}`,
      batch_no: batchNo,
      material_id: material.id,
      supplier_id: null,
      po_no: null,
      qty_small: isi,
      rack_bin_id: null,
      warehouse_zone_id: null,
      flag: 'OPENING',
      unit_status: PD_UNIT_STATUS.awaiting,
      qc_date: null,
      unit_big_snapshot: packName,
      small_per_big_snapshot: isi,
      is_leftover_wip: false,
      opening_line_id: line.id,
      capture_session_id: session.id,
      stored_at: null
    }))
  )
}

/** Cetak stiker (print = commit): satu `rm_unit` per kemasan. Cetak ulang memakai kode yang sama + satu alasan. */
export function printStickers(body: { line_ids?: string[]; reprint_reason?: string }) {
  requireAdmin('Cetak stiker pendataan awal')
  const session = requireSession('RUNNING')
  const sessionId = String(session.id)
  const lines = [...new Set(body.line_ids ?? [])].map(id => lineOf(id, sessionId))
  if (!lines.length) fail('Centang dulu material yang akan dicetak.')
  const notReady = lines.find(line => !isIncluded(line) || lineStatusOf(line) !== 'APPROVED' || !line.material_id)
  if (notReady)
    fail(
      `${String(notReady.source_name)} belum disetujui — hanya material Disetujui yang bisa dicetak.`,
      'ERR_NOT_APPROVED',
      422
    )
  const reprint = lines.filter(line => !line.needs_reprint && lineUnits(String(line.id)).length > 0)
  const reason = String(body.reprint_reason ?? '').trim()
  if (reprint.length && !(PD_REPRINT_REASONS as readonly string[]).includes(reason))
    fail('Pilih alasan cetak ulang.', 'ERR_REASON_REQUIRED')
  return inTransaction(() => {
    const labels: PdLabel[] = []
    let created = 0
    let reprinted = 0
    lines.forEach(line => {
      const existing = lineUnits(String(line.id))
      const isReprint = reprint.includes(line)
      const units = isReprint
        ? existing.sort((a, b) => String(a.qr_code).localeCompare(String(b.qr_code)))
        : createStickers(line, session)
      if (isReprint) reprinted += units.length
      else created += units.length
      updateRow('rm_opening_stock', String(line.id), {
        printed_at: iso(),
        needs_reprint: false,
        batch_no: units[0]?.batch_no ?? line.batch_no ?? null,
        ...auditFieldsForUpdate()
      })
      units.forEach(unit => labels.push(labelOf(unit, line)))
    })
    if (created)
      txLog(
        LOG_TYPE,
        'TAT-01',
        'pd-cetak',
        String(session.pdw_no),
        `Cetak ${created} stiker baru untuk ${lines.length - reprint.length} material (satu per kemasan)`
      )
    if (reprinted)
      txLog(
        LOG_TYPE,
        'TAT-01',
        'pd-cetak-ulang',
        String(session.pdw_no),
        `Cetak ulang ${reprinted} stiker untuk ${reprint.length} material — alasan: ${reason}`
      )
    return { labels, created, reprinted }
  })
}

/** Stiker yang sudah tercetak (Lihat / cetak ulang tanpa commit). */
export function stickerLabels(params: Record<string, unknown> | undefined) {
  const session = pdActiveSession()
  if (!session) return { labels: [] }
  const ids = String(params?.line_ids ?? '')
    .split(',')
    .filter(Boolean)
  const labels = ids.flatMap(id => {
    const line = findRow('rm_opening_stock', id)
    if (!line) return []
    return lineUnits(id)
      .sort((a, b) => String(a.qr_code).localeCompare(String(b.qr_code)))
      .map(unit => labelOf(unit, line))
  })
  return { labels }
}

/* ============================================================ opname pertama — Admin */

const isOperatorRm = (userId: string) =>
  readTable('mt_privileges').some(
    row => idOf(row.user) === userId && String(findRow('mt_roles', idOf(row.role))?.code) === ROLE.operator
  )

export function assignRack(body: { rack_id?: string; operator_id?: string | null }) {
  requireAdmin('Pembagian rack')
  const session = requireSession('RUNNING')
  const sessionId = String(session.id)
  const entry = pdRacks().find(item => String(item.rack.id) === String(body.rack_id ?? ''))
  if (!entry) fail('Rack tidak ditemukan.', 'ERR_NOT_FOUND', 404)
  const rackCode = String(entry.rack.code)
  if (isSent(sessionId, String(entry.rack.id)))
    fail(`Rack ${rackCode} sudah terkirim; operatornya tidak bisa diganti.`, 'ERR_RACK_SENT', 409)
  const operatorId = String(body.operator_id ?? '')
  if (operatorId && !isOperatorRm(operatorId)) fail('Pilih Operator WH RM.')
  const area = areaOf(sessionId, String(entry.rack.id))
  if (area)
    updateRow('initial_capture_area', String(area.id), { operator_id: operatorId || null, ...auditFieldsForUpdate() })
  else
    insertRow('initial_capture_area', {
      ...auditFieldsForCreate(),
      id: newId(),
      session_id: sessionId,
      rack_id: entry.rack.id,
      operator_id: operatorId || null,
      submitted_at: null
    })
  txLog(
    LOG_TYPE,
    'TAT-01',
    'pd-bagi',
    String(session.pdw_no),
    `Rack ${rackCode} ${operatorId ? `dibagikan ke ${userName(operatorId)}` : 'tidak lagi dibagikan'}`
  )
  if (operatorId)
    notify({
      to: { userIds: [operatorId] },
      type: 'CAPTURE_ASSIGNED',
      title: `Rack ${rackCode} — opname pertama`,
      body: opnameStarted(session)
        ? `Rack ${rackCode} dibagikan kepada Anda. Buka Pendataan Awal — Petugas, lalu scan posisi dan stiker.`
        : `Rack ${rackCode} dibagikan kepada Anda. Scan bisa dimulai setelah Admin WH RM menekan Mulai Opname.`,
      link: wmsRoute(PD_OPERATOR_ROUTE),
      ref: { type: 'initial_capture_session', id: sessionId }
    })
  return { rack_code: rackCode, operator_id: operatorId || null }
}

export function startOpname() {
  requireAdmin('Mulai Opname')
  const session = requireSession('RUNNING')
  const sessionId = String(session.id)
  if (opnameStarted(session)) fail('Opname pertama sudah dimulai.', 'ERR_WORKFLOW_STATE_MISMATCH', 422)
  const racks = rackProgress(sessionId)
  const missing = racks.filter(rack => !rack.operator_id)
  if (!racks.length || missing.length)
    fail(
      `Bagikan semua rack ke operator dulu (${racks.length - missing.length} dari ${racks.length}).`,
      'ERR_RACK_UNASSIGNED',
      422
    )
  if (!sessionUnits(sessionId).some(unit => unit.unit_status === PD_UNIT_STATUS.awaiting))
    fail('Belum ada stiker yang dicetak.', 'ERR_NO_STICKER', 422)
  updateRow('initial_capture_session', sessionId, {
    opname_started_at: iso(),
    opname_started_by: currentUserId(),
    ...auditFieldsForUpdate()
  })
  txLog(
    LOG_TYPE,
    'TAT-01',
    'pd-mulai-opname',
    String(session.pdw_no),
    `Opname pertama dimulai — ${racks.length} rack dibagikan ke operator`
  )
  const operators = [...new Set(racks.map(rack => String(rack.operator_id)))]
  notify({
    to: { userIds: operators },
    type: 'OPNAME_STARTED',
    title: 'Opname pertama dimulai',
    body: 'Buka Pendataan Awal — Petugas, lalu scan posisi dan stiker di rack Anda.',
    link: wmsRoute(PD_OPERATOR_ROUTE),
    ref: { type: 'initial_capture_session', id: sessionId }
  })
  return { racks: racks.length, operators: operators.length }
}

export function declareLost(body: { unit_ids?: string[]; reasons?: string[] }) {
  requireAdmin('Menyatakan kemasan hilang')
  const session = requireSession('RUNNING')
  const sessionId = String(session.id)
  if (!allSent(sessionId))
    fail(
      'Kemasan yang belum discan baru bisa dinyatakan hilang setelah semua rack terkirim.',
      'ERR_RACKS_NOT_SENT',
      422
    )
  const reasons = (body.reasons ?? []).map(reason => String(reason).trim()).filter(Boolean)
  if (!reasons.length) fail('Pilih alasan, atau tulis alasan lain.', 'ERR_REASON_REQUIRED')
  if (reasons.some(reason => reason.length > 90)) fail('Alasan maksimal 90 karakter.')
  const states = unitStateMap(sessionId)
  const units = [...new Set(body.unit_ids ?? [])]
    .map(id => findRow('rm_unit', id))
    .filter(
      (unit): unit is LocalRow =>
        Boolean(unit) &&
        idOf(unit?.capture_session_id) === sessionId &&
        unit?.unit_status === PD_UNIT_STATUS.awaiting &&
        !states.has(String(unit?.id))
    )
  if (!units.length) fail('Centang kemasan yang belum discan.')
  return inTransaction(() => {
    units.forEach(unit =>
      insertRow('initial_capture_scan', {
        ...auditFieldsForCreate(),
        id: newId(),
        session_id: sessionId,
        unit_id: unit.id,
        rack_bin_id: null,
        result: 'LOST',
        reasons,
        by_id: currentUserId(),
        at: iso(),
        undone_at: null
      })
    )
    txLog(
      LOG_TYPE,
      'TAT-01',
      'pd-hilang',
      String(session.pdw_no),
      `${units.length} kemasan dinyatakan hilang — ${reasons.join(', ')}`
    )
    return { lost: units.length }
  })
}

export function closeOpname() {
  requireAdmin('Menutup opname pertama')
  const session = requireSession('RUNNING')
  const sessionId = String(session.id)
  const summary = summaryOf(sessionId)
  if (!opnameStarted(session)) fail('Opname pertama belum dimulai.', 'ERR_WORKFLOW_STATE_MISMATCH', 422)
  if (!allSent(sessionId))
    fail(`Menunggu semua rack terkirim (${summary.racks_sent} dari ${summary.racks}).`, 'ERR_RACKS_NOT_SENT', 422)
  if (!summary.total || summary.remaining)
    fail(`${summary.remaining} kemasan belum discan. Nyatakan hilang yang tidak ditemukan.`, 'ERR_UNRESOLVED', 422)
  updateRow('initial_capture_session', sessionId, {
    session_status: 'AWAITING_APPROVAL',
    closed_at: iso(),
    closed_by: currentUserId(),
    ...auditFieldsForUpdate()
  })
  txLog(
    LOG_TYPE,
    'TAT-01',
    'pd-tutup',
    String(session.pdw_no),
    `Opname pertama ditutup dan diajukan ke Supervisor — ${summary.placed} kemasan ditempatkan, ${summary.lost} hilang`
  )
  notify({
    to: { roles: [ROLE.supervisor] },
    type: 'CAPTURE_STATUS',
    title: `Opname pertama ${String(session.pdw_no)} menunggu persetujuan`,
    body: `${summary.placed} kemasan ditempatkan, ${summary.lost} hilang — setujui atau kembalikan di Pendataan Awal.`,
    link: wmsRoute(PD_ROUTE),
    ref: { type: 'initial_capture_session', id: sessionId },
    severity: 'warning'
  })
  return { session_status: 'AWAITING_APPROVAL', ...summary }
}

/* ============================================================ opname pertama — Supervisor */

const requireSupervisor = (what: string) => requireRole(`${what} hanya oleh Supervisor WH RM.`, ROLE.supervisor)

export function approveOpname() {
  requireSupervisor('Persetujuan opname pertama')
  const session = requireSession('AWAITING_APPROVAL')
  const sessionId = String(session.id)
  const states = unitStateMap(sessionId)
  const summary = summaryOf(sessionId)
  if (summary.remaining) fail('Masih ada kemasan yang belum diselesaikan.', 'ERR_UNRESOLVED', 422)
  return inTransaction(() => {
    const now = iso()
    const patches = new Map<string, Record<string, unknown>>()
    const movements: LocalRow[] = []
    sessionUnits(sessionId)
      .filter(unit => unit.unit_status === PD_UNIT_STATUS.awaiting)
      .forEach(unit => {
        const state = states.get(String(unit.id))
        if (state?.result === 'PRESENT') {
          const bin = findRow('mt_rack_bin', idOf(state.rack_bin_id))
          const rack = bin ? findRow('mt_rack', idOf(bin.rack_id)) : undefined
          patches.set(String(unit.id), {
            unit_status: 'IN_STOCK',
            rack_bin_id: bin?.id ?? null,
            warehouse_zone_id: rack?.zone_id ?? null,
            stored_at: now,
            ...auditFieldsForUpdate()
          })
          /* Bentuk baris = `stockMovement` (c07.shared), ditulis sekaligus. */
          movements.push({
            ...auditFieldsForCreate(),
            id: newId(),
            ts: now,
            movement_type: 'OPENING',
            unit_id: unit.id,
            qty_small: num(unit.qty_small),
            from_rack_bin_id: null,
            to_rack_bin_id: bin?.id ?? null,
            from_warehouse_zone_id: null,
            to_warehouse_zone_id: rack?.zone_id ?? null,
            doc_ref: String(session.pdw_no)
          })
        } else if (state?.result === 'LOST') {
          patches.set(String(unit.id), { unit_status: PD_UNIT_STATUS.lost, ...auditFieldsForUpdate() })
        }
      })
    patchRows('rm_unit', new Set(patches.keys()), row => patches.get(String(row.id)) ?? {})
    insertRows('stock_movement', movements)
    const placed = movements.length
    const lost = patches.size - placed
    updateRow('initial_capture_session', sessionId, {
      session_status: 'COMPLETED',
      approved_at: now,
      approved_by: currentUserId(),
      result: { placed, lost },
      ...auditFieldsForUpdate()
    })
    txLog(
      LOG_TYPE,
      'TAT-01',
      'pd-setuju',
      String(session.pdw_no),
      `Opname pertama disetujui — ${placed} kemasan menjadi stok awal, ${lost} hilang`
    )
    notify({
      to: { userIds: [idOf(session.closed_by) || idOf(session.user_created)] },
      type: 'CAPTURE_STATUS',
      title: `Opname pertama ${String(session.pdw_no)} disetujui`,
      body: `${placed} kemasan aktif di Stok Inventory, ${lost} hilang.`,
      link: wmsRoute(PD_ROUTE),
      ref: { type: 'initial_capture_session', id: sessionId },
      severity: 'success'
    })
    return { session_status: 'COMPLETED', placed, lost }
  })
}

/**
 * Supervisor mengembalikan opname pertama (keputusan user 2026-10-21 #6, tutup E-1): hanya rack bermasalah yang
 * dipilih yang dibuka lagi untuk operatornya (dengan catatan, operator diberi tahu); rack lain tetap terkirim dan hasil
 * scan semua rack tetap ada. `rack_ids` wajib (minimal satu rack sesi ini yang sudah terkirim).
 */
export function returnOpname(body: { note?: string; rack_ids?: string[] }) {
  requireSupervisor('Pengembalian opname pertama')
  const session = requireSession('AWAITING_APPROVAL')
  const sessionId = String(session.id)
  const note = String(body.note ?? '').trim()
  const rackIds = [...new Set((body.rack_ids ?? []).map(String).filter(Boolean))]
  if (!rackIds.length) fail('Pilih rack yang perlu dihitung ulang.', 'ERR_RACK_REQUIRED')
  if (note.length < 3) fail('Tulis dulu apa yang perlu diperbaiki.', 'ERR_REASON_REQUIRED')
  if (note.length > 140) fail('Catatan maksimal 140 karakter.')
  const racks = pdRacks()
  const areas = rackIds.map(rackId => {
    const entry = racks.find(item => String(item.rack.id) === rackId)
    const area = areaOf(sessionId, rackId)
    if (!entry || !area) fail('Rack tidak ada di opname pertama ini.', 'ERR_NOT_FOUND', 404)
    if (!area?.submitted_at)
      fail(`Rack ${String(entry?.rack.code)} belum terkirim.`, 'ERR_WORKFLOW_STATE_MISMATCH', 422)
    return { area: area as LocalRow, code: String(entry?.rack.code) }
  })
  const now = iso()
  updateRow('initial_capture_session', sessionId, {
    session_status: 'RUNNING',
    return_note: note,
    returned_by: currentUserId(),
    returned_at: now,
    ...auditFieldsForUpdate()
  })
  areas.forEach(({ area }) =>
    updateRow('initial_capture_area', String(area.id), {
      submitted_at: null,
      /** ERD-ADD (laporan 32): status kembali per rack. */
      returned_at: now,
      returned_by: currentUserId(),
      return_note: note,
      return_count: num(area.return_count) + 1,
      ...auditFieldsForUpdate()
    })
  )
  const codes = areas.map(item => item.code)
  txLog(
    LOG_TYPE,
    'TAT-01',
    'pd-balik',
    String(session.pdw_no),
    `Opname pertama dikembalikan — rack ${codes.join(', ')} dihitung ulang: ${note}. Rack lain tetap terkirim; hasil scan tetap ada`
  )
  notify({
    to: { userIds: [idOf(session.closed_by) || idOf(session.user_created)] },
    type: 'CAPTURE_STATUS',
    title: `Opname pertama ${String(session.pdw_no)} dikembalikan`,
    body: `Rack ${codes.join(', ')} dihitung ulang: ${note}`,
    link: wmsRoute(PD_ROUTE),
    ref: { type: 'initial_capture_session', id: sessionId },
    severity: 'warning'
  })
  const byOperator = new Map<string, string[]>()
  areas.forEach(({ area, code }) => {
    const operator = idOf(area.operator_id)
    if (operator) byOperator.set(operator, [...(byOperator.get(operator) ?? []), code])
  })
  byOperator.forEach((mine, operator) =>
    notify({
      to: { userIds: [operator] },
      type: 'CAPTURE_ASSIGNED',
      title: `Rack ${mine.join(', ')} dibuka lagi`,
      body: `Supervisor meminta hitung ulang: ${note}. Periksa rack ini, lalu kirim lagi.`,
      link: wmsRoute(PD_OPERATOR_ROUTE),
      ref: { type: 'initial_capture_session', id: sessionId }
    })
  )
  return { session_status: 'RUNNING', racks_reopened: areas.length, racks: codes }
}

/* ============================================================ opname pertama — Operator (TAT-02) */

function requireOperatorSession() {
  requireRole('Scan stiker hanya untuk Operator WH RM.', ROLE.operator)
  const session = pdActiveSession()
  if (!session) fail('Pendataan awal belum dimulai.', 'ERR_NO_SESSION', 409)
  if (session.session_status !== 'RUNNING')
    fail('Hasil sudah ditutup dan menunggu persetujuan Supervisor WH RM.', 'ERR_WORKFLOW_STATE_MISMATCH', 422)
  if (!opnameStarted(session))
    fail(
      'Opname belum dimulai — Admin WH RM menekan Mulai Opname setelah semua rack dibagikan.',
      'ERR_OPNAME_NOT_STARTED',
      409
    )
  return session
}

/** Posisi rack yang boleh discan operator ini: posisi rack miliknya yang belum terkirim. */
function requireMyPosition(session: LocalRow, code: unknown) {
  const sessionId = String(session.id)
  const bin = binsOfRm().find(row => up(row.code) === up(code))
  if (!bin || bin.is_active === false || !POSITION_CODE.test(String(bin.code)))
    fail(`Kode ${up(code) || '—'} bukan posisi rak. Scan kode posisi yang menempel di rak.`, 'ERR_NOT_POSITION', 422)
  const rack = findRow('mt_rack', idOf(bin.rack_id)) as LocalRow
  const area = areaOf(sessionId, String(rack.id))
  if (idOf(area?.operator_id) !== currentUserId())
    fail(
      `Posisi ${String(bin.code)} ada di Rack ${String(rack.code)} yang tidak dibagikan kepada Anda.`,
      'ERR_NOT_YOUR_RACK',
      403
    )
  if (area?.submitted_at) fail(`Hasil Rack ${String(rack.code)} sudah dikirim.`, 'ERR_RACK_SENT', 409)
  return { bin, rack }
}

export function opPosition(body: { code?: string }) {
  const session = requireOperatorSession()
  const { bin, rack } = requireMyPosition(session, body.code)
  const state = positionStates(String(session.id)).get(String(bin.id)) ?? null
  return { position: String(bin.code), rack_code: String(rack.code), state }
}

export function opPlace(body: { position_code?: string; code?: string }) {
  const session = requireOperatorSession()
  const sessionId = String(session.id)
  const { bin } = requireMyPosition(session, body.position_code)
  const code = String(body.code ?? '').trim()
  const unit = readTable('rm_unit').find(row => up(row.qr_code) === up(code))
  if (!unit || idOf(unit.capture_session_id) !== sessionId || unit.unit_status !== PD_UNIT_STATUS.awaiting)
    fail(
      'Yang discan bukan stiker kemasan pendataan awal sesi ini. Scan stiker yang ditempel di kemasan, atau scan kode posisi rak.',
      'ERR_NOT_PD_STICKER',
      422
    )
  const states = unitStateMap(sessionId)
  const current = states.get(String(unit.id))
  if (current?.result === 'PRESENT')
    fail(
      `Stiker ini sudah discan di ${String(findRow('mt_rack_bin', idOf(current.rack_bin_id))?.code ?? '—')} oleh ${userName(current.by_id)}. Satu stiker hanya bisa punya satu posisi.`,
      'ERR_ALREADY_SCANNED',
      409
    )
  if (current?.result === 'LOST')
    updateRow('initial_capture_scan', String(current.id), { undone_at: iso(), ...auditFieldsForUpdate() })
  insertRow('initial_capture_scan', {
    ...auditFieldsForCreate(),
    id: newId(),
    session_id: sessionId,
    unit_id: unit.id,
    rack_bin_id: bin.id,
    result: 'PRESENT',
    reasons: null,
    by_id: currentUserId(),
    at: iso(),
    undone_at: null
  })
  const material = findRow('mt_material', idOf(unit.material_id))
  txLog(
    LOG_TYPE,
    'TAT-02',
    current?.result === 'LOST' ? 'pd-ubah' : 'pd-tempat',
    String(unit.qr_code),
    `${stickerNumber(String(unit.qr_code))} — ${current?.result === 'LOST' ? 'ditemukan lagi (sebelumnya hilang), ' : ''}discan di ${String(bin.code)}, isi ${num(unit.qty_small)}`
  )
  return {
    unit_id: String(unit.id),
    qr_code: String(unit.qr_code),
    number: stickerNumber(String(unit.qr_code)),
    material_name: materialName(material),
    qty: num(unit.qty_small),
    position: String(bin.code),
    was_lost: current?.result === 'LOST'
  }
}

export function opEmpty(body: { position_code?: string }) {
  const session = requireOperatorSession()
  const sessionId = String(session.id)
  const { bin } = requireMyPosition(session, body.position_code)
  const state = positionStates(sessionId).get(String(bin.id))
  if (state && state.count > 0)
    fail(`Posisi ${String(bin.code)} sudah berisi ${state.count} kemasan yang discan.`, 'ERR_POSITION_NOT_EMPTY', 409)
  insertRow('initial_capture_scan', {
    ...auditFieldsForCreate(),
    id: newId(),
    session_id: sessionId,
    unit_id: null,
    rack_bin_id: bin.id,
    result: 'EMPTY',
    reasons: null,
    by_id: currentUserId(),
    at: iso(),
    undone_at: null
  })
  txLog(LOG_TYPE, 'TAT-02', 'pd-kosong', String(session.pdw_no), `Posisi ${String(bin.code)} ditandai kosong`)
  return { position: String(bin.code) }
}

export function opUndo(body: { unit_id?: string }) {
  const session = requireOperatorSession()
  const sessionId = String(session.id)
  const state = unitStateMap(sessionId).get(String(body.unit_id ?? ''))
  if (!state || state.result !== 'PRESENT') fail('Kemasan ini belum discan.', 'ERR_NOT_FOUND', 404)
  requireMyPosition(session, findRow('mt_rack_bin', idOf(state.rack_bin_id))?.code)
  updateRow('initial_capture_scan', String(state.id), { undone_at: iso(), ...auditFieldsForUpdate() })
  const unit = findRow('rm_unit', idOf(state.unit_id))
  txLog(
    LOG_TYPE,
    'TAT-02',
    'pd-urung',
    String(unit?.qr_code ?? ''),
    `${stickerNumber(String(unit?.qr_code ?? ''))} — scan diurungkan, kembali belum discan`
  )
  return { unit_id: String(body.unit_id) }
}

export function opSubmitRack(body: { rack_id?: string }) {
  const session = requireOperatorSession()
  const sessionId = String(session.id)
  const entry = pdRacks().find(item => String(item.rack.id) === String(body.rack_id ?? ''))
  if (!entry) fail('Rack tidak ditemukan.', 'ERR_NOT_FOUND', 404)
  const area = areaOf(sessionId, String(entry.rack.id))
  if (idOf(area?.operator_id) !== currentUserId())
    fail(`Rack ${String(entry.rack.code)} tidak dibagikan kepada Anda.`, 'ERR_NOT_YOUR_RACK', 403)
  if (area?.submitted_at) fail(`Hasil Rack ${String(entry.rack.code)} sudah dikirim.`, 'ERR_RACK_SENT', 409)
  const progress = rackProgress(sessionId).find(item => item.rack_id === String(entry.rack.id))
  if (!progress || progress.visited < progress.total)
    fail(
      `Masih ${progress ? progress.total - progress.visited : '?'} posisi belum dikunjungi. Posisi tanpa barang: ketuk kotaknya lalu tandai kosong.`,
      'ERR_POSITIONS_LEFT',
      422
    )
  updateRow('initial_capture_area', String(area?.id), { submitted_at: iso(), ...auditFieldsForUpdate() })
  txLog(
    LOG_TYPE,
    'TAT-02',
    'pd-kirim',
    String(session.pdw_no),
    `Rack ${String(entry.rack.code)} dikirim — ${progress.total} posisi dikunjungi, ${progress.packages} kemasan discan`
  )
  notify({
    to: { userIds: [idOf(session.user_created)] },
    type: 'CAPTURE_STATUS',
    title: `Rack ${String(entry.rack.code)} terkirim`,
    body: `${userName(currentUserId())} mengirim hasil Rack ${String(entry.rack.code)}: ${progress.packages} kemasan di ${progress.total} posisi.`,
    link: wmsRoute(PD_ROUTE),
    ref: { type: 'initial_capture_session', id: sessionId }
  })
  return { rack_code: String(entry.rack.code), positions: progress.total, packages: progress.packages }
}

/** Layar petugas: rack milik operator ini (pembatasan yang direkomendasikan laporan 23 P-06), posisi, isi. */
export function pdOperator() {
  const session = pdActiveSession()
  const completed = readTable('initial_capture_session').some(row => row.session_status === 'COMPLETED')
  if (!session) return { session: null, completed, racks: [], demo: null }
  const sessionId = String(session.id)
  const me = currentUserId()
  const states = positionStates(sessionId)
  const unitStates = unitStateMap(sessionId)
  const units = sessionUnits(sessionId).filter(unit => unit.unit_status === PD_UNIT_STATUS.awaiting)
  const unitById = new Map(units.map(unit => [String(unit.id), unit]))
  const lines = new Map(linesOf(sessionId).map(line => [String(line.id), line]))
  const progress = rackProgress(sessionId)
  const racks = pdRacks()
    .filter(({ rack }) => idOf(areaOf(sessionId, String(rack.id))?.operator_id) === me)
    .map(({ rack, positions }) => {
      const info = progress.find(item => item.rack_id === String(rack.id))
      return {
        ...(info as NonNullable<typeof info>),
        positions: positions.map(bin => {
          const state = states.get(String(bin.id)) ?? null
          const placed = [...unitStates.values()]
            .filter(row => row.result === 'PRESENT' && idOf(row.rack_bin_id) === String(bin.id))
            .map(row => {
              const unit = unitById.get(idOf(row.unit_id))
              const line = unit ? lines.get(idOf(unit.opening_line_id)) : undefined
              const material = unit ? findRow('mt_material', idOf(unit.material_id)) : undefined
              return {
                unit_id: idOf(row.unit_id),
                qr_code: String(unit?.qr_code ?? ''),
                number: stickerNumber(String(unit?.qr_code ?? '')),
                material_name: materialName(material) || String(line?.source_name ?? ''),
                qty: num(unit?.qty_small),
                small_unit: line ? unitNameOf(line.small_unit_id) : ''
              }
            })
          return { code: String(bin.code), state, placed }
        })
      }
    })
  const pending = units.filter(unit => !unitStates.has(String(unit.id)))
  const placedAny = units.find(unit => unitStates.get(String(unit.id))?.result === 'PRESENT')
  const otherRack = pdRacks().find(({ rack }) => idOf(areaOf(sessionId, String(rack.id))?.operator_id) !== me)
  const regular = readTable('rm_unit').find(unit => unit.flag !== 'OPENING' && unit.unit_status === 'IN_STOCK')
  const fg = readTable('fg_label')[0]
  return {
    session: {
      id: sessionId,
      pdw_no: String(session.pdw_no),
      session_status: String(session.session_status),
      opname_started: opnameStarted(session),
      return_note: (session.return_note as string | null) ?? null
    },
    completed,
    racks,
    demo: {
      next_unit_code: pending[0] ? String(pending[0].qr_code) : null,
      next_unit_number: pending[0] ? stickerNumber(String(pending[0].qr_code)) : null,
      scanned_unit_code: placedAny ? String(placedAny.qr_code) : null,
      other_rack_position: otherRack?.positions[0] ? String(otherRack.positions[0].code) : null,
      regular_unit_code: regular ? String(regular.qr_code) : null,
      fg_label_code: fg ? String(fg.label_no) : null
    }
  }
}

/* ============================================================ registrasi */

export function registerPendataanAwalHandlers(mock: MockAdapter) {
  const base = '/api/v1/wms/setup-tat/pd'
  const post = <T>(path: string, run: (body: T) => unknown) =>
    mock.onPost(`${base}/${path}`).reply(config => handle(() => run(parse<T>(config.data))))
  const get = (path: string, run: (params: Record<string, unknown> | undefined) => unknown) =>
    mock.onGet(`${base}/${path}`).reply(config => handle(() => run(config.params)))
  get('state', () => pdState())
  get('units', () => pdUnits())
  get('operator', () => pdOperator())
  get('propose-code', proposeCode)
  get('check-code', checkCode)
  get('sticker-labels', stickerLabels)
  post('import-preview', importPreview)
  post('import-commit', importCommit)
  post('line-set-pack', lineSetPack)
  post('line-apply-bulk', lineApplyBulk)
  post('line-approve-bulk', lineApproveBulk)
  post('line-set-saldo', lineSetSaldo)
  post('line-skip', lineSkip)
  post('add-to-master', addToMaster)
  post('add-all-to-master', addAllToMaster)
  post('print-stickers', printStickers)
  post('assign-rack', assignRack)
  post('start-opname', startOpname)
  post('declare-lost', declareLost)
  post('close', closeOpname)
  post('approve', approveOpname)
  post('return', returnOpname)
  post('op-position', opPosition)
  post('op-place', opPlace)
  post('op-empty', opEmpty)
  post('op-undo', opUndo)
  post('op-submit-rack', opSubmitRack)
}
